import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Menu, InputGroup } from '@storable/canaveral-components';
import moment from 'moment-timezone';
import styles from './VersionDropDownButton.scss';
import cx from 'classnames';

const formatDate = (timestamp) => {
  if (!timestamp) return 'Date not available';
  const parsedDate = moment(parseInt(timestamp, 10));
  return parsedDate.isValid() ? parsedDate.format('D MMM YYYY, HH:mm:ss') : 'Date not available';
};

const getContextAndLabel = (selectedVersion, publishedVersion) => {
  if (selectedVersion) {
    return {
      label: `Page Version - ${selectedVersion.versionName}`,
      context: 'primary',
    };
  }
  if (publishedVersion) {
    return {
      label: `Page Version - ${publishedVersion.versionName}`,
      context: 'positive',
    };
  }
  return {
    label: 'Page Versions',
    context: 'secondary',
  };
};

const getItemContext = (isPublished, isSelected) => {
  if (isPublished) {
    return 'positive';
  }
  if (isSelected) {
    return 'primary';
  }
  return 'secondary';
};

const VersionItem = React.memo(({ version, isSelected, onClick }) => {
  const { versionName, changedBy, updatedAt, isPublished } = version;

  const itemClasses = cx(styles.versionItem, {
    [styles.published]: isPublished,
    [styles.selected]: isSelected,
  });


  const context = getItemContext(isPublished, isSelected);

  return (
    <Menu.Item
      className={itemClasses}
      onClick={onClick}
      context={context}
      icon="success"
    >
      <div className={styles.versionDetails}>
        <span className={styles.versionName}>{versionName}</span>
        <span>{changedBy}</span>
        <span>{`Updated At ${updatedAt}`}</span>
      </div>
    </Menu.Item>
  );
});

VersionItem.displayName = 'VersionItem';

const VersionDropDownButton = ({ majorVersions, onMajorVersionClick, selectedMajorVersion }) => {
  const [filteredVersions, setFilteredVersions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const normalizedVersions = useMemo(() =>
    majorVersions.map(version => ({
      ...version,
      isPublished: version.isPublished,
      updatedAt: formatDate(version.updatedAt),
    })),
  [majorVersions]);

  const publishedVersion = useMemo(() =>
    normalizedVersions.find(version => version.isPublished),
  [normalizedVersions]);

  const { label, context } = useMemo(() =>
    getContextAndLabel(selectedMajorVersion, publishedVersion),
  [selectedMajorVersion, publishedVersion]);

  const handleSearch = useCallback((e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchTerm(searchValue);

    const filtered = normalizedVersions.filter(version =>
      version.versionName.toLowerCase().includes(searchValue) ||
      version.changedBy.toLowerCase().includes(searchValue));
    setFilteredVersions(filtered);
  }, [normalizedVersions]);

  const handleVersionClick = useCallback((id) => {
    onMajorVersionClick(id);
  }, [onMajorVersionClick]);

  const versionsToDisplay = searchTerm ? filteredVersions : normalizedVersions;

  return (
    <Dropdown
      toggle={
        <Dropdown.Toggle
          context={context}
          pull="left"
          className={styles.toggle}
          icon="success"
        >
          {label}
        </Dropdown.Toggle>
      }
      className={styles.dropdown}
    >
      <Menu
        className={styles.menu}
        header={
          <Menu.Header title="Page Versions">
            <InputGroup>
              <InputGroup.Text
                icon="search"
                placeholder="Search versions..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </InputGroup>
          </Menu.Header>
        }
      >
        {versionsToDisplay.length > 0 ? (
          versionsToDisplay.map(version => (
            <VersionItem
              key={version.id}
              version={version}
              isSelected={selectedMajorVersion?.id === version.id}
              onClick={() => handleVersionClick(version.id)}
            />
          ))
        ) : (
          <Menu.Item>
            <div>No versions found</div>
          </Menu.Item>
        )}
      </Menu>
    </Dropdown>
  );
};

VersionDropDownButton.propTypes = {
  majorVersions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      versionName: PropTypes.string.isRequired,
      updatedAt: PropTypes.string.isRequired,
      changedBy: PropTypes.string.isRequired,
      isPublished: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  onMajorVersionClick: PropTypes.func.isRequired,
  selectedMajorVersion: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
};

export default VersionDropDownButton;
